import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../hooks/useAuth';
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaChevronDown } from 'react-icons/fa';
import { toast } from 'react-toastify';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  transition: all 0.3s ease;
  padding: 0 20px;
  
  @media (max-width: 480px) {
    max-width: 100%;
    padding: 0 5px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    max-width: 360px;
    padding: 0 10px;
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    max-width: 100%;
    padding: 0;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 15px;
  width: 100%;
  min-height: 46px;
  
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.textSecondary};
    font-size: clamp(14px, 3.5vw, 18px);
    z-index: 1;
    pointer-events: none;
    
    @media (max-width: 480px) {
      font-size: clamp(13px, 3.2vw, 14px);
      left: 8px;
    }
  }
  
  @media (max-width: 480px) {
    min-height: 38px;
    margin-bottom: 10px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    margin-bottom: 12px;
  }
`;

const Input = styled.input`
  padding: 12px 40px 12px 40px;
  width: 100%;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  transition: all 0.2s;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  height: 46px;
  /* 16px es el tamaño que previene zoom en iOS */
  font-size: 16px;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}33;
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
    
    @media (max-width: 480px) {
      font-size: 13px;
    }
    
    @media (max-width: 350px) {
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 30px 8px 30px;
    height: 38px;
    border-radius: 6px;
  }
  
  @media (max-width: 320px) {
    padding: 6px 28px 6px 28px;
    height: 36px;
    border-radius: 5px;
  }
`;

const Button = styled.button`
  padding: 12px;
  background-color: ${props => props.theme.secondary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: clamp(14px, 3.5vw, 16px);
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.secondaryHover};
  }
  
  @media (max-width: 480px) {
    padding: 8px;
    margin-top: 5px;
    font-size: 13px;
    border-radius: 6px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  color: ${props => props.theme.text};
  gap: 10px;
  
  @media (max-width: 480px) {
    font-size: clamp(12px, 3vw, 14px);
    margin-bottom: 12px;
    gap: 8px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    margin-bottom: 12px;
  }
`;

const Checkbox = styled.input`
  margin-right: 10px;
  accent-color: ${props => props.theme.secondary};
`;

const Label = styled.label`
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const WelcomeMessage = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  h3 {
    color: ${props => props.theme.text};
    margin: 0 0 8px 0;
    font-size: clamp(16px, 4vw, 18px);
    
    @media (max-width: 480px) {
      margin-bottom: 4px;
      font-size: clamp(15px, 3.8vw, 16px);
    }
  }
  
  p {
    color: ${props => props.theme.textSecondary};
    margin: 0;
    font-size: clamp(12px, 3vw, 14px);
    
    @media (max-width: 480px) {
      font-size: clamp(11px, 2.8vw, 12px);
    }
  }
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    margin-bottom: 15px;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: ${props => props.theme.textSecondary};
  padding: 4px;
  display: flex;
  align-items: center;
  z-index: 2;
  
  &:hover {
    color: ${props => props.theme.text};
  }
  
  svg {
    position: static;
    transform: none;
    left: auto;
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    right: 10px;
    padding: 3px;
    
    svg {
      font-size: 14px;
    }
  }
`;

const PasswordInputWrapper = styled(InputWrapper)`
  svg:first-child {
    z-index: 1;
  }
`;

const EmailInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 15px;
  width: 100%;
  
  & > div {
    margin-bottom: 10px;
  }
  
  & > div:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 8px;
    
    & > div {
      margin-bottom: 6px;
    }
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    margin-bottom: 12px;
    
    & > div {
      margin-bottom: 8px;
    }
  }
  
  @media (min-width: 1024px) and (max-height: 800px) {
    display: flex;
    flex-direction: column;
    grid-gap: 10px;
    
    & > div:first-child {
      width: 100%;
    }
  }
`;

const EmailInput = styled.input`
  padding: 12px 40px 12px 40px;
  width: 100%;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.theme.text};
  transition: all 0.2s;
  box-sizing: border-box;
  -webkit-appearance: none;
  appearance: none;
  height: 46px;
  padding-right: 140px;
  /* 16px es el tamaño que previene zoom en iOS */
  font-size: 16px;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}33;
    outline: none;
  }
  
  &::placeholder {
    color: ${props => props.theme.textSecondary};
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-size: 14px;
    
    @media (max-width: 480px) {
      font-size: 13px;
    }
    
    @media (max-width: 350px) {
      font-size: 12px;
    }
  }
  
  @media (max-width: 480px) {
    padding: 8px 90px 8px 30px;
    height: 38px;
    border-radius: 6px;
  }
  
  @media (max-width: 320px) {
    padding: 6px 80px 6px 28px;
    height: 36px;
    border-radius: 5px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    height: 42px;
    padding-right: 120px;
  }
`;

const EmailDomainPart = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.text};
  pointer-events: none;
  font-size: 15px;
  z-index: 1;
  opacity: 0.8;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 480px) {
    right: 8px;
    max-width: 80px;
    font-size: 12px;
  }
  
  @media (max-width: 320px) {
    right: 6px;
    max-width: 70px;
    font-size: 11px;
  }
`;

// Mover la definición de universities fuera del componente
// para que no sea recreada en cada renderizado
const universities = [
  { id: 'uach', name: 'Universidad Autónoma de Chihuahua', domain: '@uach.mx' },
  { id: 'tec', name: 'Tecnológico de Monterrey', domain: '@tec.mx' },
  { id: 'ibero', name: 'Universidad Iberoamericana', domain: '@correo.uia.mx' },
  { id: 'anahuac', name: 'Universidad Anáhuac', domain: '@anahuac.mx' }
];

const SuccessMessage = styled.div`
  text-align: center;
  margin: 20px 0;
  padding: 20px;
  background-color: #e8f5e9;
  border-radius: 8px;
  color: #2e7d32;
  
  h3 {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: clamp(16px, 4vw, 18px);
  }
  
  p {
    margin-bottom: 5px;
    font-size: clamp(12px, 3vw, 14px);
  }
  
  @media (max-width: 480px) {
    padding: 15px 10px;
    margin: 10px 0;
    
    h3 {
      font-size: clamp(15px, 3.8vw, 16px);
      margin-bottom: 8px;
    }
    
    p {
      font-size: clamp(11px, 2.8vw, 12px);
      margin-bottom: 4px;
    }
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    padding: 15px;
    margin: 10px 0;
  }
`;

const ComboboxWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 46px;
  
  @media (max-width: 480px) {
    height: 38px;
  }
`;

const ComboboxButton = styled.button`
  width: 100%;
  height: 100%;
  padding: 12px 40px 12px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  background-color: ${props => props.theme.inputBg};
  color: ${props => props.value ? props.theme.text : props.theme.textSecondary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* 16px es el tamaño que previene zoom en iOS */
  font-size: 16px;
  
  &:focus {
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 2px ${props => props.theme.primary}33;
    outline: none;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background-color: ${props => props.theme.disabled};
  }
  
  @media (max-width: 480px) {
    padding: 8px 30px 8px 10px;
    -webkit-tap-highlight-color: transparent;
    border-radius: 6px;
  }
  
  @media (max-width: 320px) {
    padding: 6px 26px 6px 8px;
    border-radius: 5px;
  }
`;

const ComboboxIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  z-index: 1;
  
  svg {
    color: ${props => props.theme.textSecondary};
    font-size: clamp(12px, 3vw, 14px);
  }
  
  @media (max-width: 480px) {
    right: 8px;
    width: 16px;
    height: 16px;
    
    svg {
      font-size: clamp(11px, 2.8vw, 12px);
    }
  }
`;

const OptionsWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 4px;
  background-color: ${props => props.theme.inputBg};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.scrollbarThumb} ${props => props.theme.scrollbarTrack};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.scrollbarTrack || props.theme.backgroundAlt};
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollbarThumb || props.theme.border};
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    max-height: 160px;
    margin-top: 2px;
    border-radius: 6px;
  }
  
  @media (min-width: 768px) and (max-height: 800px) {
    max-height: 140px;
  }
`;

const Option = styled.button`
  padding: 10px 12px;
  width: 100%;
  text-align: left;
  background-color: ${props => props.$isSelected ? `${props.theme.primary}15` : 'transparent'};
  color: ${props => props.theme.text};
  border: none;
  cursor: pointer;
  font-size: clamp(14px, 3.5vw, 15px);
  display: flex;
  align-items: center;
  transition: background-color 0.2s;
  
  &:hover, &:focus {
    background-color: ${props => props.$isSelected ? `${props.theme.primary}25` : props.theme.backgroundAlt};
    outline: none;
  }
  
  @media (max-width: 480px) {
    padding: 7px 8px;
    font-size: 13px;
  }
`;

const RegisterForm = ({ onClose }) => {
  const [username, setUsername] = useState('');
  const [emailPrefix, setEmailPrefix] = useState('');
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [randomizedUniversities, setRandomizedUniversities] = useState([]);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const comboboxRef = useRef(null);
  
  // Ref para medir el ancho del dominio
  const domainRef = React.useRef(null);
  
  // Generate a unique ID for this form instance
  const formId = React.useMemo(() => `register-form-${Math.random().toString(36).substr(2, 9)}`, []);
  
  const { register } = useAuth();
  
  // Randomize universities on component mount
  useEffect(() => {
    // Asegurarse de que estamos creando una nueva copia del array
    const shuffled = [...universities];
    
    // Algoritmo de Fisher-Yates para mezclar el array de forma más efectiva
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    
    // Actualizar el estado con el array mezclado
    setRandomizedUniversities(shuffled);
    
  }, []); // Ahora no necesitamos incluir universities porque es constante
  
  // Cerrar el combobox cuando se hace clic fuera de él
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsComboboxOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);
  
  // Manejar selección de universidad desde el combobox
  const handleUniversitySelect = (universityId) => {
    setSelectedUniversity(universityId);
    setIsComboboxOpen(false);
    
    // Enfocar el campo de email después de un breve retraso
    setTimeout(() => {
      const emailInput = document.getElementById(`${formId}-email`);
      if (emailInput && universityId) {
        emailInput.focus();
      }
    }, 50);
  };
  
  // Obtener el nombre de la universidad seleccionada
  const getSelectedUniversityName = () => {
    if (!selectedUniversity) return '';
    const university = randomizedUniversities.find(u => u.id === selectedUniversity);
    return university ? university.name : '';
  };
  
  // Get the domain for the selected university
  const getUniversityDomain = () => {
    if (!selectedUniversity) return '';
    const university = universities.find(u => u.id === selectedUniversity);
    return university ? university.domain : '';
  };
  
  // Get the complete email with domain
  const getCompleteEmail = () => {
    if (!emailPrefix || !selectedUniversity) return '';
    return `${emailPrefix}${getUniversityDomain()}`;
  };
  
  // Validate email prefix to prevent invalid characters
  const validateEmailPrefix = (value) => {
    // Regex to allow only letters, numbers, dots, underscores and hyphens
    const validPrefixRegex = /^[a-zA-Z0-9._-]+$/;
    return validPrefixRegex.test(value);
  };
  
  // Handle email prefix change with validation
  const handleEmailPrefixChange = (e) => {
    const value = e.target.value.toLowerCase().trim();
    
    // Only update if the value is valid or empty
    if (value === '' || validateEmailPrefix(value)) {
      setEmailPrefix(value);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get complete email
    const completeEmail = getCompleteEmail();
    
    // Validación básica
    if (!username.trim() || !completeEmail || !password || !confirmPassword) {
      toast.error('Por favor, completa todos los campos.');
      return;
    }
    
    // Validar longitud de nombre de usuario
    if (username.length < 3) {
      toast.error('El nombre de usuario debe tener al menos 3 caracteres.');
      return;
    }
    
    if (username.length > 30) {
      toast.error('El nombre de usuario no puede exceder los 30 caracteres.');
      return;
    }
    
    // Validate email prefix format
    if (emailPrefix && !validateEmailPrefix(emailPrefix)) {
      toast.error('El correo electrónico solo puede contener letras, números, puntos, guiones bajos y guiones.');
      return;
    }
    
    // Validar longitud del correo electrónico
    if (completeEmail.length > 100) {
      toast.error('El correo electrónico no puede exceder los 100 caracteres.');
      return;
    }
    
    // Validate university selection
    if (!selectedUniversity) {
      toast.error('Por favor, selecciona tu universidad.');
      return;
    }
    
    // Verify that the complete email ends with the proper domain
    const university = universities.find(u => u.id === selectedUniversity);
    if (!university) {
      toast.error('Universidad seleccionada no válida.');
      return;
    }
    
    // Validate that email has the correct domain
    if (!completeEmail.endsWith(university.domain)) {
      toast.error(`El correo debe terminar con ${university.domain}`);
      return;
    }
    
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    
    if (password.length > 72) {
      toast.error('La contraseña no puede exceder los 72 caracteres.');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden.');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Debes aceptar los términos y condiciones.');
      return;
    }
    
    setLoading(true);
    
    // Extraer el ID de la universidad del dominio de correo
    const universityId = selectedUniversity;
    
    // La función register del AuthContext devuelve un objeto, no lanza excepciones
    const result = await register(username, completeEmail, password, universityId);
    
    if (result.success) {
      if (result.requiresVerification) {
        // Mostrar mensaje de verificación
        setRegistrationSuccess(true);
        setRegisteredEmail(completeEmail);
        toast.success('¡Registro exitoso! Revisa tu correo para verificar tu cuenta.');
      } else {
        // Si no requiere verificación, cerrar el modal
        toast.success('¡Registro exitoso! Bienvenido a Sapotix.');
        onClose();
      }
    } else {
      toast.error(result.message || 'Error en el registro. Intenta con otro correo o nombre de usuario.');
    }
    
    setLoading(false);
  };
  
  // Si el registro fue exitoso y requiere verificación, mostrar mensaje
  if (registrationSuccess) {
    return (
      <SuccessMessage>
        <h3>¡Revisa tu correo! 📧</h3>
        <p>Te hemos enviado un enlace de verificación a:</p>
        <p><strong>{registeredEmail}</strong></p>
        <p>Por favor, haz clic en el enlace para verificar tu cuenta.</p>
        <p><strong>⚠️ No olvides revisar tu carpeta de SPAM si no encuentras el correo ⚠️</strong></p>
        <p>El correo puede tardar unos minutos en llegar.</p>
        <Button onClick={onClose}>Entendido</Button>
      </SuccessMessage>
    );
  }
  
  return (
    <Form onSubmit={handleSubmit}>
      <WelcomeMessage>
        <h3>¡Únete al chisme anónimo! 🎭</h3>
        <p>Crea tu cuenta para compartir y descubrir secretos</p>
      </WelcomeMessage>
      
      <InputWrapper>
        <FaUser />
        <Input 
          type="text"
          placeholder="Elige un usuario secreto"
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase().trim())}
          disabled={loading}
          id={`${formId}-username`}
          name="register-username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
        />
      </InputWrapper>
      
      <EmailInputGroup>
        <InputWrapper>
          <ComboboxWrapper ref={comboboxRef}>
            <ComboboxButton
              type="button"
              onClick={() => setIsComboboxOpen(!isComboboxOpen)}
              disabled={loading}
              value={selectedUniversity}
              aria-haspopup="listbox"
              aria-expanded={isComboboxOpen}
            >
              {selectedUniversity 
                ? getSelectedUniversityName() 
                : "Selecciona tu universidad"}
            </ComboboxButton>
            <ComboboxIcon>
              <FaChevronDown />
            </ComboboxIcon>
            <OptionsWrapper $isOpen={isComboboxOpen}>
              {randomizedUniversities.map(uni => (
                <Option
                  key={uni.id}
                  type="button"
                  onClick={() => handleUniversitySelect(uni.id)}
                  $isSelected={selectedUniversity === uni.id}
                  tabIndex={isComboboxOpen ? 0 : -1}
                >
                  {uni.name}
                </Option>
              ))}
            </OptionsWrapper>
          </ComboboxWrapper>
        </InputWrapper>
        
        {/* Campo oculto para engañar al autocompletado */}
        <input 
          type="text" 
          id="email_fake" 
          name="email_fake"
          style={{ display: 'none' }} 
          autoComplete="email" 
        />
        
        <InputWrapper>
          <FaEnvelope />
          <EmailInput 
            type="text"
            value={emailPrefix}
            onChange={handleEmailPrefixChange}
            placeholder="Usuario Institucional"
            disabled={loading || !selectedUniversity}
            id={`${formId}-email`}
            name="register-email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            autoComplete="new-password"
            data-form-type="other"
          />
          {selectedUniversity && <EmailDomainPart ref={domainRef}>{getUniversityDomain()}</EmailDomainPart>}
        </InputWrapper>
      </EmailInputGroup>
      
      <PasswordInputWrapper>
        <FaLock />
        <Input 
          type={showPassword ? "text" : "password"}
          placeholder="Crea una contraseña secreta"
          value={password}
          onChange={(e) => setPassword(e.target.value.trim())}
          disabled={loading}
          id={`${formId}-password`}
          name="register-password"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
        />
        <PasswordToggle
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
          {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
        </PasswordToggle>
      </PasswordInputWrapper>
      
      <InputWrapper>
        <FaLock />
        <Input 
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirma tu contraseña"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value.trim())}
          disabled={loading}
          id={`${formId}-confirm-password`}
          name="register-confirm-password"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
        />
        <PasswordToggle
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
        >
          {showConfirmPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
        </PasswordToggle>
      </InputWrapper>
      
      <CheckboxContainer>
        <Checkbox 
          type="checkbox" 
          id={`${formId}-terms-checkbox`} 
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          disabled={loading}
          name="register-terms"
        />
        <Label htmlFor={`${formId}-terms-checkbox`}>
          Acepto mantener el anonimato y los <a href="/terms" target="_blank">términos del chisme</a> 🤐
        </Label>
      </CheckboxContainer>
      
      <Button type="submit" disabled={loading}>
        {loading ? 'Creando tu perfil secreto...' : '¡Unirme al chisme!'}
      </Button>
    </Form>
  );
};

export default RegisterForm;